import type { DiaDeProgresso } from '@titularis/api-client';
import { somarDias } from './serieDoRitmo';

/**
 * Previsão de conclusão dos comentários.
 *
 * A conta é simples de propósito - o que falta, dividido pelo ritmo medido -,
 * e as decisões que valem estão em quais números entram nela.
 *
 * **Peças, e não questões.** Peça é a unidade que se move todo dia; questão
 * comentada anda em degraus e passa dias parada, então dividir por ela daria um
 * ritmo que oscila entre zero e infinito. No fim das contas dá no mesmo: quando
 * a última peça é escrita, a última questão fecha.
 *
 * **Escrever e validar contam igual.** As duas são trabalho de gente e as duas
 * esvaziam o mesmo estoque: quando alguém assina uma sugestão da máquina, a peça
 * sai de "a fazer" exatamente como se tivesse sido escrita do zero. Somar só as
 * escritas punha as duas pontas da divisão em unidades diferentes - o que falta
 * já tratava a peça revisada como pronta, e o ritmo não contava a revisão -, e a
 * data só sabia andar para longe. Em 27 e 28/08/2026 o time validou 132 peças e
 * não escreveu nenhuma: a tela respondia "ninguém escreveu, sem ritmo, não há
 * data" com todo mundo revisando, e no período inteiro a previsão errava 39 dias
 * para mais.
 *
 * **Dias de calendário, e não dias trabalhados.** É a decisão que mais muda o
 * resultado. Dividir só pelos dias em que alguém trabalhou dá um ritmo mais
 * bonito e uma data que só valeria se ninguém mais tirasse folga - e os dias que
 * faltam terão fins de semana como os que passaram. Contar o calendário inteiro
 * embute as paradas, que é o que faz a data ser uma data e não um desejo.
 *
 * **A projeção parte de hoje, não do fim do período medido.** O período serve
 * para medir a velocidade; o que falta é estoque de agora.
 */

export type MotivoSemPrevisao = 'concluido' | 'sem-ritmo';

export interface Previsao {
  /** Peças sem assinatura de gente, no filtro atual da tela. */
  restantes: number;
  /** Dias de calendário no período medido, incluindo os parados. */
  diasMedidos: number;
  /** Peças que uma pessoa assinou dentro do período, escrevendo ou validando. */
  pecasNoPeriodo: number;
  /** Peças por dia de calendário. */
  porDia: number;
  /** Dias necessários no mesmo ritmo. Nulo quando não há previsão. */
  diasRestantes: number | null;
  /** A data prevista, `YYYY-MM-DD`. Nula quando não há previsão. */
  dia: string | null;
  /** Por que não houve previsão, quando não houve. */
  motivo: MotivoSemPrevisao | null;
}

export interface EntradaDaPrevisao {
  /** A série diária, como a API devolve: uma linha por dia e por pessoa. */
  dados: DiaDeProgresso[];
  /** Início do período medido, `YYYY-MM-DD`. */
  de: string;
  /** Fim do período medido, `YYYY-MM-DD`, inclusive. */
  ate: string;
  /** Peças que faltam. */
  restantes: number;
  /** Hoje, em Brasília. */
  hoje: string;
}

/** Dias de calendário entre duas datas, contando as duas pontas. */
function diasEntre(de: string, ate: string): number {
  const inicio = Date.parse(`${de}T12:00:00Z`);
  const fim = Date.parse(`${ate}T12:00:00Z`);
  return Math.floor((fim - inicio) / 86_400_000) + 1;
}

export function preverConclusao({ dados, de, ate, restantes, hoje }: EntradaDaPrevisao): Previsao {
  /*
   * A soma junta as pessoas: a API devolve uma linha por dia e por autor, e o
   * ritmo que interessa aqui é o do time.
   *
   * Escrita e validação somam sem risco de contar duas vezes: a série as separa
   * porque o gráfico quer ver uma encolher e a outra crescer, mas elas são
   * mutuamente exclusivas por construção - a mesma peça é redigida ou é
   * conferida, nunca as duas no mesmo evento.
   */
  const pecasNoPeriodo = dados
    .filter((linha) => linha.dia >= de && linha.dia <= ate)
    .reduce((soma, linha) => soma + linha.pecas + linha.pecasValidadas, 0);

  const diasMedidos = Math.max(1, diasEntre(de, ate));
  const porDia = pecasNoPeriodo / diasMedidos;

  const vazio = { restantes, diasMedidos, pecasNoPeriodo, porDia };

  // Já acabou: não há o que prever, e "0 dias" seria uma data no lugar de uma
  // notícia boa.
  if (restantes <= 0) {
    return { ...vazio, diasRestantes: null, dia: null, motivo: 'concluido' };
  }

  /*
   * Ritmo zero não vira data. Dividir por zero daria infinito, e mostrar uma
   * data no ano 200 mil seria pior que dizer que não dá para prever - número
   * absurdo na tela é lido como defeito, e esconde a informação real, que é
   * "ninguém escreveu nem revisou nada no período escolhido".
   */
  if (pecasNoPeriodo <= 0) {
    return { ...vazio, diasRestantes: null, dia: null, motivo: 'sem-ritmo' };
  }

  // Arredonda para cima: meio dia de trabalho restante ainda é um dia no
  // calendário.
  const diasRestantes = Math.ceil(restantes / porDia);

  return { ...vazio, diasRestantes, dia: somarDias(hoje, diasRestantes), motivo: null };
}
